// Phase 9: Bank Reconciliation workspace. Scoped to one real Accounting
// Bank Account (and its linked engine ledger) at a time - never a generic
// "Main Bank" ledger (item 1). This is an entirely new page built on the
// new /society/accounting/reconciliation/* endpoints; the legacy Phase 1/2
// reconciliation UI (a free-text "account" string matched only against
// Billing payments) is left in place and reachable at
// bank-reconciliation-legacy, never deleted, per this engagement's standing
// "never remove old routes" convention.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { toast } from "sonner";
import { SectionCard, EmptyState, DetailField, ReasonDialog } from "./shared";
import SearchableSelect from "./SearchableSelect";
import { ACCOUNTING_BASE } from "./accountingNav";
import { formatCurrency, formatDateDMY } from "./LineGrid";

const TIER_LABEL = { exact: "Exact", strong: "Strong", possible: "Possible", manual_review: "Manual review", manual: "Manual" };
const TIER_TONE = {
  exact: "bg-emerald-50 text-emerald-700", strong: "bg-emerald-50 text-emerald-700",
  possible: "bg-amber-50 text-amber-700", manual_review: "bg-red-50 text-red-700", manual: "bg-[#EEF2FF] text-[#4338CA]",
};

function TierBadge({ tier }) {
  if (!tier) return null;
  return <span className={`rounded-sm px-2 py-0.5 text-[11px] font-semibold ${TIER_TONE[tier] || "bg-[#F3F4F6] text-[#6B7280]"}`}>{TIER_LABEL[tier] || tier}</span>;
}

const TABS = [
  { id: "workspace", label: "Workspace" },
  { id: "import", label: "Import Statement" },
  { id: "history", label: "History" },
];

export default function BankReconciliationWorkspacePage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [bankAccountId, setBankAccountId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [tab, setTab] = useState("workspace");
  const [view, setView] = useState("unmatchedBank");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Import wizard state
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [columnMap, setColumnMap] = useState({});
  const [statementName, setStatementName] = useState("");

  // Completion state
  const [asOfDate, setAsOfDate] = useState("");
  const [completeNote, setCompleteNote] = useState("");
  const [history, setHistory] = useState([]);

  // Draft-voucher dialog
  const [draftRow, setDraftRow] = useState(null);
  const [draftLedgerId, setDraftLedgerId] = useState("");
  const [draftNarration, setDraftNarration] = useState("");

  // Unmatch reason dialog
  const [unmatchRow, setUnmatchRow] = useState(null);
  const [reopenCompletion, setReopenCompletion] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [accRes, ledRes] = await Promise.all([
          api.get("/society/accounting/engine/bank-accounts").catch(() => ({ data: [] })),
          api.get("/society/accounting/engine/ledgers").catch(() => ({ data: [] })),
        ]);
        const rows = Array.isArray(accRes.data) ? accRes.data : [];
        setAccounts(rows);
        setLedgers(Array.isArray(ledRes.data) ? ledRes.data : []);
        if (rows.length === 1) setBankAccountId(rows[0].id);
      } catch { /* handled via empty state */ }
    })();
  }, []);

  const accountOptions = useMemo(() => accounts.map((a) => ({
    value: a.id, label: `${a.bankName} — ${a.accountName || ""} •••• ${a.accountNumber || ""}`.replace(/\s+/g, " ").trim(),
  })), [accounts]);
  const ledgerOptions = useMemo(() => ledgers.filter((l) => l.active !== false).map((l) => ({ value: l.id, label: l.name, group: l.ledgerGroup })), [ledgers]);

  const loadWorkspace = async () => {
    if (!bankAccountId) { setData(null); return; }
    try {
      setLoading(true);
      const res = await api.get("/society/accounting/reconciliation/workspace", { params: { bankAccountId, fromDate, toDate } });
      setData(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not load reconciliation workspace"));
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadWorkspace(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [bankAccountId, fromDate, toDate]);

  const loadHistory = async () => {
    try {
      const res = await api.get("/society/accounting/reconciliation/history", { params: bankAccountId ? { bankAccountId } : {} });
      setHistory(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(formatErr(err, "Could not load reconciliation history"));
    }
  };
  useEffect(() => { if (tab === "history") loadHistory(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab, bankAccountId]);

  const runAutoMatch = async () => {
    if (!bankAccountId) return;
    try {
      setBusy(true);
      const res = await api.post("/society/accounting/reconciliation/auto-match", null, { params: { bankAccountId, fromDate, toDate } });
      const r = res.data || {};
      toast.success(`Checked ${r.checked} — auto-matched ${r.autoMatched}, ${r.suggested} suggested, ${r.ambiguous} ambiguous`);
      await loadWorkspace();
    } catch (err) {
      toast.error(formatErr(err, "Auto-match failed"));
    } finally {
      setBusy(false);
    }
  };

  const confirmMatch = async (row, candidate) => {
    try {
      setBusy(true);
      await api.post(`/society/accounting/reconciliation/statement-rows/${row.id}/match`, { voucherId: candidate.voucherId, voucherLineId: candidate.voucherLineId, note: "Confirmed from suggested match" });
      toast.success("Matched");
      await loadWorkspace();
    } catch (err) {
      toast.error(formatErr(err, "Could not match"));
    } finally {
      setBusy(false);
    }
  };

  const doUnmatch = async (reason) => {
    try {
      setBusy(true);
      await api.post(`/society/accounting/reconciliation/statement-rows/${unmatchRow.id}/unmatch`, { reason });
      toast.success("Unmatched");
      setUnmatchRow(null);
      await loadWorkspace();
    } catch (err) {
      toast.error(formatErr(err, "Could not unmatch"));
    } finally {
      setBusy(false);
    }
  };

  const createDraft = async () => {
    if (!draftRow || !draftLedgerId) return;
    try {
      setBusy(true);
      const res = await api.post(`/society/accounting/reconciliation/statement-rows/${draftRow.id}/create-voucher-draft`, { voucherType: draftRow.debit > 0 ? "PAYMENT" : "RECEIPT", ledgerId: draftLedgerId, narration: draftNarration });
      toast.success("Draft voucher created");
      setDraftRow(null); setDraftLedgerId(""); setDraftNarration("");
      navigate(`${ACCOUNTING_BASE}/vouchers/${res.data.id}/edit`);
    } catch (err) {
      toast.error(formatErr(err, "Could not create draft voucher"));
    } finally {
      setBusy(false);
    }
  };

  // --- Import wizard ---
  const onFileChosen = async (f) => {
    setFile(f);
    setPreview(null);
    setColumnMap({});
    const isXlsx = /\.xlsx?$|\.xlsm$/i.test(f.name);
    try {
      setBusy(true);
      let body;
      if (isXlsx) {
        const buf = await f.arrayBuffer();
        const b64 = btoa(new Uint8Array(buf).reduce((s, b) => s + String.fromCharCode(b), ""));
        body = { fileName: f.name, fileBase64: b64 };
      } else {
        body = { fileName: f.name, csvText: await f.text() };
      }
      const res = await api.post("/society/accounting/reconciliation/import/preview", body);
      setPreview({ ...res.data, ...body });
    } catch (err) {
      toast.error(formatErr(err, "Could not read this file"));
    } finally {
      setBusy(false);
    }
  };

  const canImport = bankAccountId && preview && columnMap.date && (columnMap.debit || columnMap.credit || columnMap.amount);

  const runImport = async () => {
    if (!canImport) return;
    try {
      setBusy(true);
      const res = await api.post("/society/accounting/reconciliation/import", {
        bankAccountId, fileName: preview.fileName, csvText: preview.csvText || "", fileBase64: preview.fileBase64 || "",
        columnMap, statementName,
      });
      toast.success(`Imported ${res.data.rowCount} row(s)${res.data.possibleDuplicates ? `, ${res.data.possibleDuplicates} flagged as possible duplicates` : ""}`);
      setFile(null); setPreview(null); setColumnMap({}); setStatementName("");
      setTab("workspace");
      await loadWorkspace();
    } catch (err) {
      toast.error(formatErr(err, "Import failed"));
    } finally {
      setBusy(false);
    }
  };

  // --- Completion ---
  const runComplete = async (acknowledge) => {
    try {
      setBusy(true);
      await api.post("/society/accounting/reconciliation/complete", { bankAccountId, asOfDate: asOfDate || toDate, note: completeNote, acknowledgeDifference: acknowledge });
      toast.success("Reconciliation completed");
      setCompleteNote("");
      await loadHistory();
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail.includes("differ by") && !acknowledge) {
        toast.error(`${detail} Add a note and click Complete again to acknowledge.`);
      } else {
        toast.error(formatErr(err, "Could not complete reconciliation"));
      }
    } finally {
      setBusy(false);
    }
  };

  const doReopenCompletion = async (reason) => {
    try {
      setBusy(true);
      await api.patch(`/society/accounting/reconciliation/completions/${reopenCompletion.id}/reopen`, { reason });
      toast.success("Reopened");
      setReopenCompletion(null);
      await loadHistory();
    } catch (err) {
      toast.error(formatErr(err, "Could not reopen"));
    } finally {
      setBusy(false);
    }
  };

  const rowsForView = data ? (data[view] || []) : [];

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Bank Reconciliation</h2>
        <p className="mt-1 text-sm text-[#6B7280]">One real Bank Account at a time, matched against posted vouchers using amount, date, cheque/UTR/reference and description together - never amount alone.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-sm border border-[#E5E7EB] bg-white p-4">
        <div className="w-80 space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Bank Account</div>
          <SearchableSelect value={bankAccountId} onChange={setBankAccountId} options={accountOptions} placeholder="Select bank account" />
        </div>
        <div className="w-40 space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">From Date</div>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div className="w-40 space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">To Date</div>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <div className="ml-auto flex gap-2 border-b-0">
          {TABS.map((t) => (
            <Button key={t.id} type="button" variant={tab === t.id ? "default" : "outline"} size="sm" onClick={() => setTab(t.id)}>{t.label}</Button>
          ))}
        </div>
      </div>

      {!bankAccountId ? (
        <EmptyState>Select a Bank Account to begin reconciling.</EmptyState>
      ) : tab === "workspace" ? (
        <>
          {data && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-6">
              <DetailField label="Book Entries" value={data.summary.bookCount} />
              <DetailField label="Bank Entries" value={data.summary.bankCount} />
              <DetailField label="Matched" value={data.summary.matchedCount} />
              <DetailField label="Unmatched Book" value={data.summary.unmatchedBookCount} />
              <DetailField label="Unmatched Bank" value={data.summary.unmatchedBankCount} />
              <DetailField label="Possible Duplicates" value={data.summary.duplicateCount} />
            </div>
          )}
          <SectionCard
            title="Reconciliation views"
            actions={<Button size="sm" disabled={busy} onClick={runAutoMatch}>Run Auto-Match</Button>}
          >
            <div className="mb-4 flex flex-wrap gap-2">
              {[
                ["unmatchedBank", "Unmatched Bank"], ["unmatchedBook", "Unmatched Book"], ["matched", "Matched"],
                ["possibleDuplicates", "Possible Duplicates"], ["bookEntries", "All Book Entries"], ["statementEntries", "All Statement Entries"],
              ].map(([id, label]) => (
                <button key={id} type="button" onClick={() => setView(id)} className={`rounded-sm px-3 py-1.5 text-xs font-semibold ${view === id ? "bg-[#111827] text-white" : "bg-[#F3F4F6] text-[#374151]"}`}>{label}</button>
              ))}
            </div>

            {loading ? (
              <div className="text-sm text-[#6B7280]">Loading...</div>
            ) : view === "bookEntries" || view === "unmatchedBook" ? (
              <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
                <Table>
                  <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Voucher</TableHead><TableHead>Particulars</TableHead><TableHead>Party</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {rowsForView.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>{formatDateDMY(r.voucherDate)}</TableCell>
                        <TableCell><button type="button" className="font-semibold text-[#111827] hover:underline" onClick={() => navigate(`${ACCOUNTING_BASE}/vouchers/${r.voucherId}`)}>{r.voucherNumber || "(draft)"}</button></TableCell>
                        <TableCell>{r.narration}</TableCell>
                        <TableCell>{r.partyName || "-"}</TableCell>
                        <TableCell className="text-right">{r.debit ? formatCurrency(r.debit) : "—"}</TableCell>
                        <TableCell className="text-right">{r.credit ? formatCurrency(r.credit) : "—"}</TableCell>
                        <TableCell>{r.matched ? <span className="text-xs font-semibold text-emerald-700">Matched</span> : <span className="text-xs font-semibold text-amber-700">Not in statement</span>}</TableCell>
                      </TableRow>
                    ))}
                    {rowsForView.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-[#6B7280]">No rows.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
                <Table>
                  <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Narration</TableHead><TableHead>Ref/Cheque/UTR</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead><TableHead>Match</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {rowsForView.map((r) => (
                      <TableRow key={r.id} className={r.isPossibleDuplicate ? "bg-amber-50/40" : ""}>
                        <TableCell>{formatDateDMY(r.transactionDate)}</TableCell>
                        <TableCell className="max-w-[220px] truncate">{r.narration}</TableCell>
                        <TableCell className="text-xs text-[#6B7280]">{r.referenceNo || r.chequeNo || r.utr || "-"}</TableCell>
                        <TableCell className="text-right">{r.debit ? formatCurrency(r.debit) : "—"}</TableCell>
                        <TableCell className="text-right">{r.credit ? formatCurrency(r.credit) : "—"}</TableCell>
                        <TableCell>
                          {r.matchStatus === "matched" ? (
                            <div className="flex items-center gap-2">
                              <TierBadge tier={r.matchTier} />
                              <button type="button" className="text-xs font-semibold text-[#111827] hover:underline" onClick={() => navigate(`${ACCOUNTING_BASE}/vouchers/${r.matchedVoucherId}`)}>View voucher</button>
                            </div>
                          ) : r.suggestedMatches && r.suggestedMatches.length > 0 ? (
                            <div className="space-y-1">
                              {r.suggestedMatches.slice(0, 3).map((c, ci) => (
                                <div key={ci} className="flex items-center gap-2 text-xs">
                                  <TierBadge tier={c.tier} />
                                  <span className="text-[#6B7280]">{c.voucherNumber || "(draft)"} — {c.signals.join(", ") || "amount only"}</span>
                                  <Button size="sm" variant="outline" disabled={busy} onClick={() => confirmMatch(r, c)}>Confirm</Button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-[#9CA3AF]">No suggestions</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {r.matchStatus === "matched" ? (
                            <Button variant="outline" size="sm" disabled={busy} onClick={() => setUnmatchRow(r)}>Unmatch</Button>
                          ) : (
                            <Button variant="outline" size="sm" disabled={busy || !!r.draftVoucherId} onClick={() => { setDraftRow(r); setDraftNarration(r.narration || ""); }}>
                              {r.draftVoucherId ? "Draft created" : "Create draft"}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {rowsForView.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-[#6B7280]">No rows.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Complete reconciliation" subtitle="The book-vs-bank difference is always shown here, never hidden.">
            <div className="grid gap-3 sm:grid-cols-4">
              <div className="space-y-1">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">As Of Date</div>
                <Input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} />
              </div>
              <div className="sm:col-span-2 space-y-1">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Note (required if there is a difference)</div>
                <Input value={completeNote} onChange={(e) => setCompleteNote(e.target.value)} placeholder="e.g. pending cheque not yet cleared" />
              </div>
              <div className="flex items-end">
                <Button disabled={busy || !asOfDate} onClick={() => runComplete(!!completeNote)} className="w-full">Complete Reconciliation</Button>
              </div>
            </div>
          </SectionCard>
        </>
      ) : tab === "import" ? (
        <SectionCard title="Import bank statement" subtitle="CSV or Excel (.xlsx) - map your file's own columns below, nothing is hardcoded to one bank's format.">
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Statement file</div>
                <Input type="file" accept=".csv,.xlsx,.xlsm" onChange={(e) => e.target.files[0] && onFileChosen(e.target.files[0])} />
              </div>
              <div className="space-y-1">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Statement name (optional)</div>
                <Input value={statementName} onChange={(e) => setStatementName(e.target.value)} placeholder="e.g. August 2026 statement" />
              </div>
            </div>

            {preview && (
              <div className="space-y-3">
                <div className="text-sm font-semibold text-[#111827]">{preview.totalRows} row(s) detected. Map the columns below:</div>
                <div className="grid gap-3 sm:grid-cols-3">
                  {preview.canonicalFields.filter((f) => f !== "amount" || !preview.headers.some((h) => columnMap.debit === h || columnMap.credit === h)).map((field) => (
                    <div key={field} className="space-y-1">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">{field}{["date"].includes(field) ? " *" : ""}</div>
                      <select className="h-9 w-full rounded-sm border border-[#D1D5DB] bg-white px-2 text-sm" value={columnMap[field] || ""} onChange={(e) => setColumnMap((m) => ({ ...m, [field]: e.target.value }))}>
                        <option value="">(not mapped)</option>
                        {preview.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
                  <Table>
                    <TableHeader><TableRow>{preview.headers.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
                    <TableBody>
                      {preview.sampleRows.map((r, i) => (
                        <TableRow key={i}>{preview.headers.map((h) => <TableCell key={h} className="text-xs">{r[h]}</TableCell>)}</TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <Button disabled={!canImport || busy} onClick={runImport}>Import Statement</Button>
              </div>
            )}
          </div>
        </SectionCard>
      ) : (
        <SectionCard title="Reconciliation history">
          <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
            <Table>
              <TableHeader><TableRow><TableHead>As Of</TableHead><TableHead className="text-right">Book Closing</TableHead><TableHead className="text-right">Bank Closing</TableHead><TableHead className="text-right">Difference</TableHead><TableHead>Note</TableHead><TableHead>Status</TableHead><TableHead>Completed By</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell>{formatDateDMY(h.asOfDate)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(h.bookClosingBalance)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(h.bankClosingBalance)}</TableCell>
                    <TableCell className={`text-right font-semibold ${Math.abs(h.difference) >= 0.01 ? "text-red-600" : "text-emerald-700"}`}>{formatCurrency(h.difference)}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-xs text-[#6B7280]">{h.note || "-"}</TableCell>
                    <TableCell className="text-xs">{h.status}</TableCell>
                    <TableCell className="text-xs">{h.completedBy}</TableCell>
                    <TableCell className="text-right">
                      {h.status === "completed" && <Button size="sm" variant="outline" onClick={() => setReopenCompletion(h)}>Reopen</Button>}
                    </TableCell>
                  </TableRow>
                ))}
                {history.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-sm text-[#6B7280]">No completed reconciliations yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </SectionCard>
      )}

      <ReasonDialog
        open={!!unmatchRow} onOpenChange={(v) => !v && setUnmatchRow(null)} busy={busy}
        title="Unmatch this statement row?" description="The earlier match stays in the audit history forever - this only clears the current link so the row can be re-matched."
        confirmLabel="Unmatch" onConfirm={doUnmatch}
      />
      <ReasonDialog
        open={!!reopenCompletion} onOpenChange={(v) => !v && setReopenCompletion(null)} busy={busy}
        title="Reopen this reconciliation?" description="Reopening is permission-gated and fully audited."
        confirmLabel="Reopen" onConfirm={doReopenCompletion}
      />

      {draftRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDraftRow(null)}>
          <div className="w-full max-w-md rounded-sm bg-white p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 text-sm font-extrabold text-[#111827]">Create draft voucher for this statement entry</div>
            <p className="mb-3 text-xs text-[#6B7280]">This creates a DRAFT voucher only - it is never auto-posted. Review and post it from All Vouchers, then match it here.</p>
            <div className="space-y-3">
              <div className="space-y-1">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Other side ledger (e.g. Bank Charges, Bank Interest)</div>
                <SearchableSelect value={draftLedgerId} onChange={setDraftLedgerId} options={ledgerOptions} placeholder="Select ledger" />
              </div>
              <div className="space-y-1">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Narration</div>
                <Input value={draftNarration} onChange={(e) => setDraftNarration(e.target.value)} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDraftRow(null)}>Cancel</Button>
                <Button disabled={!draftLedgerId || busy} onClick={createDraft}>Create Draft</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
